import { useRollbar } from '@rollbar/react'
import { useStripe } from '@stripe/react-stripe-js'
import { Stripe } from '@stripe/stripe-js'
import { BadRequest, InternalError, NetworkError } from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { PaymentButtonInterface } from 'common/types/entities/PaymentButtonInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import usePayment, {
  selectors as paymentSelectors,
} from 'publisher/hooks/usePayment'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { OldEntityInterface } from '../../../common/types/entities/OldEntityInterface'
import { optInFail } from '../../actions/optInActions'
import * as api from '../../api/stripe'
import { confirmPaymentIntent } from '../../api/stripe'
import { IntentTypeEnum } from '../../enums/IntentTypeEnum'
import usePage, { selectors as pageSelectors } from '../usePage'
import usePaymentSubmit from './usePaymentSubmit'

function getStripeConfirmHandler(stripe: Stripe, type: IntentTypeEnum) {
  return type === IntentTypeEnum.setup
    ? stripe.confirmBancontactSetup
    : stripe.confirmBancontactPayment
}

export default function useStripeBancontactPaymentHandler(
  entity:
    | OldEntityInterface
    | PaymentButtonInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) {
  const dispatch = useDispatch()
  const { t } = useTypedTranslation()
  const rollbar = useRollbar()
  const stripe = useStripe()
  const purchaseProcessId = usePayment(paymentSelectors.getPurchaseProcessId)
  const pageId = usePage(pageSelectors.getPageId)
  const { errors, setErrors, isLoading, submit } = usePaymentSubmit(entity)

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (!stripe) {
      return
    }

    await submit(
      async body => {
        const { data } = await api.buyMainOffer(pageId, purchaseProcessId, {
          payment_form: { ...body },
        })
        const { intentSecret, intentType, redirect } = data

        if (intentSecret) {
          const stripeConfirmHandler = getStripeConfirmHandler(
            stripe,
            intentType,
          )

          const { error } = await stripeConfirmHandler(intentSecret, {
            payment_method: {
              billing_details: {
                name: body.customer.fields.first_name,
                email: body.customer.fields.email,
              },
            },
            return_url: redirect,
          })

          if (!error) {
            const { data } = await confirmPaymentIntent(
              pageId,
              purchaseProcessId,
            )
            // redirect to upSell or thank you page
            if (data.redirect) {
              window.location.href = data.redirect
            }
          } else {
            setErrors([error.message as string])
          }
        } else {
          // redirect to upSell or thank you page
          window.location.href = redirect
        }
      },
      (error, body) => {
        if (error instanceof BadRequest) {
          setErrors(error.response.data.errors.common)
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (error instanceof InternalError) {
          setErrors([t('core.error.title')])
        } else {
          if (error instanceof Error) {
            rollbar.captureEvent(body!, 'debug')
            rollbar.error('Stripe Bancontact payment failed', error as Error)
          }
        }
      },
    )
  }

  return { errors, isLoading, handleSubmit }
}
